import type {
  Id,
  TimelineGroup,
  TimelineItem,
} from './react-calendar-timeline';
import type {
  PortCallEventType,
  SingleEventType,
  StaticEvent,
  TimeData,
} from '../core/TimeData';
import {
  type Leg,
  type OffHireEvent,
  type PortCallEvent,
  type SingleEvent,
  type WaitEvent,
} from '../core/TimeData';
import type {
  DocumentType as PortCallDocument,
  OpenPositionsFragment,
  OrderId,
  ScenarioTimeline_CoaFragment,
  VesselArea,
  ShipId,
  VesselReservationFragment,
} from '@seaber/common/graphql/types';
import type { CSSProperties } from 'react';
import type { CapacityOverCause } from './utils/capacityOver';
import type { SuggestionDraftWarning } from './utils/VesselDraftUtils';
import type { Suggestion } from '../core/suggestions/getSuggestions';
import type { LocationId, TCOrderEntry } from '@seaber/common/core/entries';
import type { Vessel } from '../core/Vessel';
import type { EventTimeline } from '../core/Timelines';
import type { ScenarioOrder } from '../../lib/planningData';
import type { TimelineDiffState } from '../ScenarioUtil';
import type { ScenarioType } from './ScenarioMergeTimeline';
import type { computeTceForTimeData } from '../shipment/TCE';
import type { CoasById } from './utils/TimelineFreightCost';
import type { WorldscaleFreightRateMap } from '../../lib/worldscaleFreight';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../Scenario';
import type {
  ExtraVoyageMetrics,
  VoyagePerformanceMetrics,
} from '../core/Core.voyage-metrics';
import type { ScenarioTimelineProps } from './ScenarioTimeline';
import type { IncomingContractType } from '../order/OrderTableUtils';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { Pnl } from '../pnl/ui/context';
import type { VesonBunker } from '../integrations/VesonRob';

export enum ItemType {
  SCHEDULED_ORDER = 'TC',
  EVENT = 'SE',
  SUGGESTION = 'SUGG',
  OFFHIRE = 'O',
  SPOT = 'S',
  COA = 'C',
  OPEN = 'OPEN',
  RESERVATION = 'RESERVE',
  COMPLETED = '$',
  TC_IN = 'TC_IN',
}

type TimelineItemBase = {
  groupObj?: TimelineGroup<ScenarioTimelineGroupFields>;
  zIndex?: CSSProperties['zIndex'] | undefined;
  width?: number;
  isCompressedView?: ScenarioTimelineProps['isCompressedView'];
  actuallySelected: boolean;
} & (
  | {
      isShadow: true;
      originalItemId: Id;
      originalContract?: string;
    }
  | {
      isShadow?: false;
    }
);

export type TimelineVoyage = TimelineItemBase & {
  type: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER;
  vessel?: Vessel;
  orders: ScenarioOrder[];
  events: TimeData[];
  implicitEvents: (Leg | WaitEvent)[];
  isCollapsed: boolean; // A collapsed item is intact and may contain several orders, if not collapsed then this item is the result of de-collapsing a shipment. Should affect context menu options.
  selected?: boolean;
  isShared: boolean; // Is every event in the batch shared
  zIndex?: CSSProperties['zIndex'] | undefined;
  onSwapScheduleEntry?: ScenarioTimelineProps['onSwapScheduleEntry'];
  onSwapPortCall?: ScenarioTimelineProps['onSwapPortCall'];
  onToggleShipment?: TimelineToggle['onToggleShipment'];
  organizations: ScenarioTimelineProps['organizations'];
  isToggleExpanded: boolean;
  dragging?: boolean;
  enablePortCallUpdates?: boolean;
  enableVoyageCharterUpdates?: boolean;
  originalContract?: string;
  combinedDiff?: TimelineDiffState;
  scenarioType?: ScenarioType;
  tceData?: ReturnType<typeof computeTceForTimeData>;
  metrics?: VoyagePerformanceMetrics;
  isFirstShipmentEvent: boolean;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  shipmentPnl?: Pnl;
  vesonBunkerData?: VesonBunker;
  coasById?: CoasById;
  worldscaleFreightRates?: WorldscaleFreightRateMap;
  shipmentFreightByShipmentClientId?: Map<ClientId, ScenarioShipmentFreight>;
  shipmentExtraFreightsByShipmentClientId?: Map<
    ClientId,
    ScenarioShipmentExtraFreight[]
  >;
  /** Draft violations this box's cargo is answerable for, each anchored to a port call the box shows. */
  draftWarnings: SuggestionDraftWarning[];
};

export type TimelineCompletedVoyage = Omit<TimelineItemBase, 'groupObj'> & {
  type: ItemType.COMPLETED;
  id: string;
  cargo: string;
  isShadow: false;
  isCollapsed: true;
  portCalls: string[];
  orders: ScenarioOrder[];
  isShared?: boolean;
  combinedDiff?: TimelineDiffState;
};

export type TimelineEvent = TimelineItemBase & {
  type: ItemType.EVENT;
  eventType: SingleEventType;
  vessel: Vessel;
  selected?: boolean;
  dragging?: boolean;
  timeData: TimeData<SingleEvent>;
  locationName: string;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  portCallEvents?: Record<PortCallEventType, TimeData<PortCallEvent>>;
};

export type TimelineOffHireEvent = TimelineItemBase & {
  type: ItemType.OFFHIRE;
  vessel: Vessel;
  timeData: TimeData<OffHireEvent>;
  locationAName: string;
  locationBName: string;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineTcInEvent = TimelineItemBase & {
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  isLastScheduleItem?: boolean;
  timeData: TimeData<StaticEvent>;
  type: ItemType.TC_IN;
};

export type TimelineOpenPosition = TimelineItemBase & {
  type: ItemType.OPEN;
  openPosition: OpenPositionsFragment;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineVesselReservation = TimelineItemBase & {
  type: ItemType.RESERVATION;
  vesselReservation: VesselReservationFragment;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineSuggestion = TimelineItemBase & {
  type: ItemType.SUGGESTION;
  suggestion: Suggestion;
  /** Lazily computes the TCE of the shipment box this suggestion produces once
   * accepted, via the box's path so it matches the box at displayed precision. Called
   * only when a suggestion's tooltip is shown (on hover), so the per-suggestion
   * timeline rebuild + PnL + TCE is not paid eagerly for every suggestion. Returns
   * `null` when the resulting voyage has no measurable target shipment slice. */
  getResultingVoyageTce: (suggestion: Suggestion) => number | null;
  /** Suggestion may be expanded; only render the given entries from the whole suggestion */
  entries: TCOrderEntry[];
  onSelect: ScenarioTimelineProps['onSuggestionSelect'];
  onCreateEstimate: ScenarioTimelineProps['createEstimateTimelineFromSuggestion'];
  onDismiss: ScenarioTimelineProps['onDismissSuggestion'];
  start: number;
  end: number;
  suggestionIndex: number;
  vesselId: ShipId;
  isBestFound?: boolean;
  onToggleShipment?: TimelineToggle['onToggleShipment'];
  isToggleExpanded: boolean;
  showInquiries: boolean;
  scheduleContext: ScenarioTimelineProps['scheduleContext'];
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  /** Grouping by first load port */
  suggestionGroupKey: string;
  firstLoadPortName: string;
  isFirstInGroup: boolean;
  isGroupExpanded: boolean;
  totalSuggestionsInGroup: number;
  shownSuggestionsInGroup: number;
  onExpandGroup: (groupKey: string, expand: boolean) => void;
  onShowMoreInGroup: (groupKey: string) => void;
};

type TimelineToggle = TimelineItemBase & {
  isToggleExpanded: boolean;
  onToggleShipment: (isCollapsed: boolean) => void;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type ScenarioTimelineGroupFields = {
  stackItems?: boolean;
  timeline: EventTimeline & {
    isCollapsed?: boolean;
    canCollapse?: boolean;
    coaId?: number;
    coa?: ScenarioTimeline_CoaFragment;
  };
  suggestions: Suggestion[];
  tce?: number | undefined;
  tceDelta?: number | undefined;
  estimateIndex?: number;
};

export type ScenarioTimelineItem = TimelineItem<
  | TimelineVoyage
  | TimelineCompletedVoyage
  | TimelineEvent
  | TimelineSuggestion
  | TimelineOffHireEvent
  | TimelineOpenPosition
  | TimelineVesselReservation
  | TimelineTcInEvent
>;

export type ScenarioTimelineGroup = TimelineGroup<ScenarioTimelineGroupFields>;

/**
 * Extracts estimateIdOrScenario from a timeline item's groupObj.
 * Items without groupObj (e.g. completed voyages) default to 'scenario'.
 */
export function getEstimateIdOrScenario(
  item: ScenarioTimelineItem,
): ClientId | 'scenario' {
  return 'groupObj' in item && item.groupObj
    ? item.groupObj.timeline.estimateIdOrScenario
    : 'scenario';
}

export type IndicatorData = {
  type:
    | 'ballast'
    | 'laden'
    | 'load'
    | 'discharge'
    | 'idle'
    | 'single'
    | 'portCall';
  subtype?: string;
  rowType?: string;
  startTime: number;
  duration: number;
  orderId?: number;
  assortments?: Array<{
    assortmentName?: string;
    duration: number;
    amount: number;
    amountUnit: string;
    orderId?: number;
  }>;
} & Pick<
  IndicatorTooltipContent,
  | 'portName'
  | 'assortmentName'
  | 'timezone'
  | 'from'
  | 'to'
  | 'amount'
  | 'amountUnit'
  | 'totalDuration'
  | 'eta'
  | 'etd'
  | 'loadCommence'
>;

export type ViolationIndicatorData = {
  type: 'load_violation' | 'discharge_violation';
  startTime: number;
  duration: number;
  portName: string;
  locationId: LocationId;
  voyageCharterId?: number;
};

export interface VoyageAssortment {
  assortmentName: string;
  amount: number;
  amountUnit: string;
  incomingContractType: IncomingContractType | null;
  cargoDeckOption: string | null;
}

export interface VoyagePortCall {
  canSwapLeftShipments: Array<ScenarioOrder['shipmentId']>;
  canSwapRightShipments: Array<ScenarioOrder['shipmentId']>;
  clientId: ClientId | null;
  events: TimeData[];
  hasCargoEvents: boolean;
  portId: LocationId;
  portName: string;
  locode: string | null;
  maxDwt: number | null;
  portCallFiles: { id: number; note?: string | null; type: PortCallDocument }[];
  startTime: number;
  orderId?: number;
  start?: number;
}

export interface VoyageLeg {
  originPortCallId: ClientId;
  originPortName: string;
  destinationPortCallId: ClientId;
  destinationPortName: string;
  availableMassCapacity: number | null;
  availableMassCapacityPercentage: number | null;
  availableVolumeCapacity: number | null;
  availableVolumeCapacityPercentage: number | null;
  /** According to min margins */
  overCapacityMass: number;
  /** According to min margins */
  overCapacityVolume: number;
  unmetPlannedMass: number;
  unmetLoadedMass: number;
  unmetPlannedVolume: number;
  unmetLoadedVolume: number;
  unmetOrders: UnmetOrder[];
  vesselMassCapacity: number | null;
  vesselVolumeCapacity: number | null;
  /** Aggregated truthful cause of this leg's under-loads: a shared reached capacity, port draft, or
   * a neutral below-ordered when the unmet orders' causes disagree or are unattributable. */
  unmetCause: CapacityOverCause | null;
  hasUnallocatedOrders: boolean;
}

export interface UnmetOrder {
  id: OrderId;
  name: string;
  planned: number;
  loaded: number;
  unit: string;
  percentLoaded: number;
}

export type ShipmentIntegrationStatus = null | {
  scenarioDiffersFromIntegration: Set<ScenarioOrder['shipmentId']>;
  actualOrderIdsByShipment: Map<ScenarioOrder['shipmentId'], Set<number>>;
  expectedOrderIdsByShipment: Map<ScenarioOrder['shipmentId'], Set<number>>;
  extraOrderIdsByShipment: DefaultMap<ScenarioOrder['shipmentId'], number[]>;
  missingOrderIdsByShipment: DefaultMap<ScenarioOrder['shipmentId'], number[]>;
};

export type ScheduleItem = ScenarioTimelineItem & {
  type:
    | ItemType.SPOT
    | ItemType.COA
    | ItemType.SCHEDULED_ORDER
    | ItemType.EVENT
    | ItemType.OFFHIRE
    | ItemType.RESERVATION
    | ItemType.OPEN
    | ItemType.TC_IN;
};

export interface IndicatorTooltipContent {
  duration?: number;
  startTime?: number;
  title: string;
  portName?: string;
  assortmentName?: string;
  timezone?: string;
  from?: string;
  to?: string;
  amount?: number;
  amountUnit?: string;
  totalDuration?: number;
  orderId?: number;
  assortments?: Array<{
    assortmentName?: string;
    duration: number;
    amount: number;
    amountUnit: string;
    orderId?: number;
  }>;
  violationIndicatorType?: ViolationIndicatorData['type'];
  totalVoyageDuration?: number;
  eventIndicatorType?: IndicatorData['type'];
  /** Port call arrival time (raw ms); formatted with `timezone` in the tooltip. */
  eta?: number;
  /** Port call departure time (raw ms); formatted with `timezone` in the tooltip. */
  etd?: number;
  /** Operations start ("Load commence", raw ms); formatted with `timezone`. */
  loadCommence?: number;
}

export interface BunkerBatch {
  name: string;
  amount: number;
  price: number;
}

export interface BunkerInventoryByGrade {
  name: string;
  totalAmount: number;
  batches: BunkerBatch[];
}

export interface BunkerInventory {
  bunkerOpen: BunkerInventoryByGrade[];
  bunkerClose: BunkerInventoryByGrade[];
}

export interface VoyageTooltipContent {
  breakEvenFreight?: number;
  title: string;
  voyageName?: string;
  vesselName?: string;
  vesselOrders: string[];
  vesselVolume?: string;
  vesselCapacity?: string;
  eta?: string;
  etd?: string;
  totalPlannedVolume?: string;
  totalPlannedMass?: string;
  massCapacityUsed?: string;
  assigner?: string;
  isPortCostMissing?: boolean;
  totalCost?: string;
  vcp?: number;
  externalShipmentId?: string;
  extraMetrics?: ExtraVoyageMetrics;
  bunkerInventory?: BunkerInventory;
  portDraft?: number;
  isPortDraftMissing?: boolean;
}

export type EcaSeaArea = Exclude<VesselArea, VesselArea.ALL_AREAS>;
